import axios, { AxiosInstance, AxiosError } from 'axios'
import { getToken, removeToken, refreshToken } from './auth'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api'

/**
 * Creates axios instance with base configuration
 * @returns Axios instance
 */
function createApiClient(): AxiosInstance {
  const token = getToken()
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  })

  /**
   * Request interceptor to add JWT token
   */
  client.interceptors.request.use(
    config => {
      const token = getToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    error => {
      return Promise.reject(error)
    }
  )

  /**
   * Response interceptor to handle 401 errors and token refresh
   */
  client.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as any

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true

        try {
          const newToken = await refreshToken()
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return client(originalRequest)
        } catch (refreshError) {
          removeToken()
          window.location.href = '/login'
          return Promise.reject(refreshError)
        }
      }

      return Promise.reject(error)
    }
  )

  return client
}

const apiClient = createApiClient()

export { apiClient }

/**
 * Gets all yards
 * @returns Promise with array of yard objects
 */
export function getYards() {
  return apiClient.get('/yards')
}

/**
 * Gets hives for a yard grouped by size
 * @param id - Yard ID
 * @returns Promise with grouped hives
 */
export function getYardHives(id: string) {
  return apiClient.get(`/yards/${id}/hives`)
}

/**
 * Gets yard by ID
 * @param id - Yard ID
 * @returns Promise with yard object
 */
export function getYardById(id: string) {
  return apiClient.get(`/yards/${id}`)
}

/**
 * Creates a new yard
 * @param yardData - Yard data object
 * @returns Promise with created yard object
 */
export function createYard(yardData: unknown) {
  return apiClient.post('/yards', yardData)
}

/**
 * Updates a yard
 * @param id - Yard ID
 * @param yardData - Yard data object
 * @returns Promise with updated yard object
 */
export function updateYard(id: string, yardData: unknown) {
  return apiClient.put(`/yards/${id}`, yardData)
}

/**
 * Deletes a yard
 * @param id - Yard ID
 * @returns Promise with deletion result
 */
export function deleteYard(id: string) {
  return apiClient.delete(`/yards/${id}`)
}

/**
 * Gets all inventory items
 * @param params - Query parameters
 * @returns Promise with array of inventory items
 */
export function getInventory(params?: unknown) {
  return apiClient.get('/inventory', { params })
}

/**
 * Gets all teams
 * @returns Promise with array of team objects
 */
export function getTeams() {
  return apiClient.get('/teams')
}

/**
 * Gets dashboard metrics
 * @returns Promise with dashboard metrics
 */
export function getDashboardMetrics() {
  return apiClient.get('/dashboard/metrics')
}

/**
 * Gets team by ID
 * @param id - Team ID
 * @returns Promise with team object
 */
export function getTeamById(id: string) {
  return apiClient.get(`/teams/${id}`)
}

/**
 * Creates a new team
 * @param teamData - Team data object
 * @returns Promise with created team object
 */
export function createTeam(teamData: { name: string; memberIds?: string[] }) {
  return apiClient.post('/teams', teamData)
}

/**
 * Updates a team
 * @param id - Team ID
 * @param teamData - Team data object
 * @returns Promise with updated team object
 */
export function updateTeam(id: string, teamData: { name?: string; memberIds?: string[] }) {
  return apiClient.put(`/teams/${id}`, teamData)
}

export function deleteTeam(id: string) {
  return apiClient.delete(`/teams/${id}`)
}

/**
 * Gets weight records for a specific yard
 * @param yardId - Yard ID
 * @returns Promise with array of weight records sorted by date
 */
export function getWeightRecordsByYard(yardId: string) {
  return apiClient.get(`/weight-records/yard/${yardId}`)
}

/**
 * Gets average weight data grouped by yard
 * @returns Promise with array of yard weight averages
 */
export function getWeightAverages() {
  return apiClient.get('/weight-records/averages')
}

/**
 * Creates a new weight record
 * @param data - Weight record data
 * @returns Promise with created weight record
 */
export function createWeightRecord(data: { yardId: string; date: string; weight: number; unit?: string; notes?: string }) {
  return apiClient.post('/weight-records', data)
}
